import { Injectable, signal } from '@angular/core';
import { Coordinates, Lead } from '../../lead.model';
import { first, Observable, of, ReplaySubject } from 'rxjs';
import { LeadsHttpService } from './leads-http.service';
import { LeadReservationsService } from '../lead-reservations/lead-reservations.service';
import { GeolocationService } from '../geolocation/geolocation.service';
import { SnackbarService } from '../snackbar/snackbar.service';
import { HttpErrorResponse } from '@angular/common/http';

interface AllLeadsInformation {
  coordinates: Coordinates;
  radiusInKilometers: number;

  leadsMap: Map<string, Lead>;
  selectedLeads: Set<string>;
}

@Injectable({
  providedIn: 'root',
})
export class LeadService {
  //this subject indicates that new leads were fetched
  private readonly allLeadsChanges = new ReplaySubject<void>(1);
  public readonly allLeadsChanges$ = this.allLeadsChanges.asObservable();

  private readonly selectedLeadsChanges = new ReplaySubject<void>(1);
  public readonly selectedLeadsChanges$ =
    this.selectedLeadsChanges.asObservable();

  private allLeadsInformation: AllLeadsInformation | undefined = undefined;

  private readonly initialRadiusInKilometers: number = 100;

  // Signal for leads with error status
  public readonly errorLeads = signal<Lead[]>([]);

  constructor(
    private readonly leadsHttpService: LeadsHttpService,
    private readonly leadsReservationsService: LeadReservationsService,
    private readonly geolocationService: GeolocationService,
    private readonly snackbarService: SnackbarService,
  ) {
  }

  initialFetch(): void {
    this.geolocationService.getPosition().then((position) => {
      this.fetchLeads(
        position.coords.latitude,
        position.coords.longitude,
        this.initialRadiusInKilometers,
      );
    });
  }

  getRadiusInKilometers(): number {
    if (this.allLeadsInformation) {
      return this.allLeadsInformation.radiusInKilometers;
    }
    throw new Error(
      'Cannot access radius since there is no data fetched from backend',
    );
  }

  getCoordinates(): Coordinates {
    if (this.allLeadsInformation) {
      return this.allLeadsInformation.coordinates;
    }
    throw new Error(
      'Cannot access coordinates since there is no data fetched from backend',
    );
  }

  deleteLeads(): void {
    this.allLeadsInformation = undefined;
    this.allLeadsChanges.next();
    this.selectedLeadsChanges.next();
  }

  fetchLeads(
    latitude: number,
    longitude: number,
    radiusInKilometers: number,
  ): void {
    this.leadsHttpService
      .fetchLeads(latitude, longitude, radiusInKilometers)
      .pipe(first())
      .subscribe({
        next: (leads: Lead[]) => {
          const leadsMap = new Map<string, Lead>();
          const selectedLeads = new Set<string>();

          for (const lead of leads) {
            leadsMap.set(lead.id, lead);
          }

          this.allLeadsInformation = {
            coordinates: {
              latitude: latitude,
              longitude: longitude,
            },
            radiusInKilometers: radiusInKilometers,
            leadsMap: leadsMap,
            selectedLeads: selectedLeads,
          };
          this.allLeadsChanges.next();
          this.selectedLeadsChanges.next();
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse && error.status === 422) {
            this.snackbarService.show('components.map.fetch-leads.too-many-leads', 5000);
          }
        },
      });
  }

  getLead(id: string): Lead | undefined {
    return this.allLeadsInformation?.leadsMap.get(id);
  }

  getLeads(): Lead[] {
    if (this.allLeadsInformation) {
      return Array.from(this.allLeadsInformation.leadsMap.values());
    }
    return [];
  }

  selectLead(lead: Lead): void {
    if (this.allLeadsInformation) {
      this.allLeadsInformation.selectedLeads.add(lead.id);
      this.selectedLeadsChanges.next();
    }
  }

  isSelected(leadId: string): boolean {
    if (this.allLeadsInformation) {
      return this.allLeadsInformation.selectedLeads.has(leadId);
    }
    return false;
  }

  getSelectedLeads(): Lead[] {
    if (this.allLeadsInformation) {
      return Array.from(this.allLeadsInformation.selectedLeads).map(
        (leadId) => {
          const lead = this.getLead(leadId);
          if (lead === undefined) {
            throw new Error(
              'Selected leads contains an ID that was not found in leadsMap',
            );
          }
          return lead;
        },
      );
    }
    return [];
  }

  removeSelectedLead(lead: Lead) {
    if (this.allLeadsInformation) {
      this.allLeadsInformation.selectedLeads.delete(lead.id);
      this.selectedLeadsChanges.next();
    }
  }

  /**
   * Removes a lead from both the full lead list and the selection,
   * e.g. after its examination has been confirmed by the backend.
   */
  removeLead(lead: Lead): void {
    if (this.allLeadsInformation) {
      this.allLeadsInformation.leadsMap.delete(lead.id);
      this.allLeadsInformation.selectedLeads.delete(lead.id);
      this.allLeadsChanges.next();
      this.selectedLeadsChanges.next();
    }
  }

  async removeAllSelectedLeads() {
    this.allLeadsInformation?.selectedLeads.clear();
    this.selectedLeadsChanges.next();
  }

  hasSelectedLeads(): boolean {
    return this.allLeadsInformation
      ? this.allLeadsInformation.selectedLeads.size > 0
      : false;
  }

  reserveSelectedLeads(): Observable<string[]> {
    const leadIds = this.getSelectedLeads().map((lead: Lead) => lead.id);
    if (leadIds.length === 0) {
      return of([]);
    }
    return this.leadsReservationsService.requestReservationForLeads(leadIds);
  }

  unreserveSelectedLeads(): Observable<void> {
    const leadIds = this.getSelectedLeads().map((lead: Lead) => lead.id);
    if (leadIds.length === 0) {
      return of(void 0);
    }
    return this.leadsReservationsService.removeReservationForLeads(leadIds);
  }

  fetchErrorLeads(): void {
    this.leadsHttpService
      .fetchLeadsWithErrorStatus()
      .pipe(first())
      .subscribe((leads: Lead[]) => {
        this.errorLeads.set(leads);
      });
  }
}
